import React, {useMemo, useCallback, useState} from 'react';
import styles from './PartnersTable.module.scss';
import {TableColumnConfig, Text, Spin, Button, DropdownMenu, Icon} from '@gravity-ui/uikit';
import {DataTable, formatDateToLocalDate} from '../../shared';
import {PartnerModel} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';
import {useNavigate} from 'react-router';
import {TrashBin} from '@gravity-ui/icons';
import {DeletePartnerModal} from '../../features';

interface PartnersTableProps {
  partners: PartnerModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
}

export const PartnersTable = ({
  partners,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: PartnersTableProps) => {
  const SKELETON_ROWS = 5;
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingPartnerId, setDeletingPartnerId] = useState<string | null>(null);

  const handleRowClick = useCallback(
    (partner: PartnerModel) => {
      navigate(`/project/partners/${partner.id}`);
    },
    [navigate],
  );

  const handleDeletePartner = useCallback((partner: PartnerModel) => {
    setDeletingPartnerId(partner.id);
    setIsDeleteModalOpen(true);
  }, []);

  const columns = useMemo<TableColumnConfig<PartnerModel>[]>(
    () => [
      {
        id: 'title',
        name: 'Название',
        template: (item: PartnerModel) => <Text variant="body-1">{item.title}</Text>,
      },
      {
        id: 'priority',
        name: 'Порядок показа',
        template: (item: PartnerModel) => <Text variant="body-1">{item.priority}</Text>,
      },
      {
        id: 'createdAt',
        name: 'Дата добавления',
        template: (item: PartnerModel) => <Text variant="body-1">{formatDateToLocalDate(item.createdAt)}</Text>,
      },
      {
        id: 'more',
        name: () => <div className={styles.tableHeader}></div>,
        template: (item: PartnerModel) => (
          <div className={styles.moreCell} onClick={(e) => e.stopPropagation()}>
            <DropdownMenu
              items={[
                {
                  iconStart: <Icon size={16} data={TrashBin} />,
                  action: () => handleDeletePartner(item),
                  text: 'Удалить',
                  className: styles.dropdownItemDelete,
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [handleDeletePartner],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<PartnerModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={partners || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Партнеры не найдены"
        emptyStateDescription="Партнеры с таким названием не найдены. Попробуйте изменить запрос"
        onRowClick={handleRowClick}
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}

      <DeletePartnerModal open={isDeleteModalOpen} setOpen={setIsDeleteModalOpen} partnerId={deletingPartnerId} />
    </div>
  );
};
